import { Calendar, ChevronDown, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export type DateRangePreset = '7d' | '30d' | '90d' | 'all';

export interface AnalyticsDateRange {
  startDate?: number;
  endDate?: number;
  label: string;
  preset?: DateRangePreset;
}

interface AnalyticsDateFilterProps {
  value: AnalyticsDateRange;
  onChange: (range: AnalyticsDateRange) => void;
}

const PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

const CUSTOM_LABEL = 'Custom range';

function timestampToDateString(ts: number): string {
  const d = new Date(ts);
  return d.toISOString().slice(0, 10);
}

function dateStringToStartOfDay(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00');
  return d.getTime();
}

function dateStringToEndOfDay(dateStr: string): number {
  const d = new Date(dateStr + 'T23:59:59.999');
  return d.getTime();
}

function formatDateRangeLabel(startDate: number, endDate: number): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function isCustomRange(value: AnalyticsDateRange): boolean {
  return value.label.includes('–') && value.startDate != null && value.endDate != null;
}

export function AnalyticsDateFilter({ value, onChange }: AnalyticsDateFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'presets' | 'custom'>('presets');
  const [customStart, setCustomStart] = useState(() =>
    value.startDate ? timestampToDateString(value.startDate) : ''
  );
  const [customEnd, setCustomEnd] = useState(() =>
    value.endDate ? timestampToDateString(value.endDate) : ''
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handlePresetSelect = (preset: DateRangePreset) => {
    const range = presetToAnalyticsDateRange(preset);
    onChange(range);
    setIsOpen(false);
    setView('presets');
  };

  const handleCustomRangeClick = () => {
    setView('custom');
    if (value.startDate && value.endDate) {
      setCustomStart(timestampToDateString(value.startDate));
      setCustomEnd(timestampToDateString(value.endDate));
    } else if (!customStart && !customEnd) {
      const today = timestampToDateString(Date.now());
      setCustomStart(today);
      setCustomEnd(today);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      const startDate = dateStringToStartOfDay(customStart);
      const endDate = dateStringToEndOfDay(customEnd);
      if (startDate <= endDate) {
        const label = formatDateRangeLabel(startDate, endDate);
        onChange({ startDate, endDate, label });
        setIsOpen(false);
        setView('presets');
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setView('presets');
  };

  const canApplyCustom = customStart && customEnd && customStart <= customEnd;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="analytics-select-date-range"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Date range: ${value.label}`}
        className={`flex items-center gap-2 px-3 py-2 bg-white border rounded-xl hover:bg-gray-50 transition-all text-sm ${
          isOpen ? 'border-gray-400 ring-2 ring-gray-100' : 'border-gray-200'
        }`}
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="text-gray-700 font-medium">{value.label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-10 lg:hidden" onClick={handleClose} />

          {/* Dropdown */}
          <div
            role="listbox"
            aria-label="Date range options"
            className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-20 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
          >
            {view === 'presets' ? (
              <div className="p-1">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    role="option"
                    aria-selected={value.preset === preset.value}
                    onClick={() => handlePresetSelect(preset.value)}
                    data-testid={`analytics-date-option-${preset.value}`}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-colors ${
                      value.preset === preset.value && !isCustomRange(value)
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className={value.preset === preset.value && !isCustomRange(value) ? 'font-medium' : ''}>
                      {preset.label}
                    </span>
                    {value.preset === preset.value && !isCustomRange(value) && (
                      <Check className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                ))}
                <button
                  role="option"
                  aria-selected={isCustomRange(value)}
                  onClick={handleCustomRangeClick}
                  data-testid="analytics-date-option-custom"
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-colors border-t border-gray-100 ${
                    isCustomRange(value) ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className={isCustomRange(value) ? 'font-medium' : ''}>{CUSTOM_LABEL}</span>
                  {isCustomRange(value) && <Check className="w-4 h-4 text-gray-600" />}
                </button>
              </div>
            ) : (
              <div className="p-3 space-y-3" data-testid="analytics-date-custom-picker">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Start date</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    data-testid="analytics-date-input-start"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">End date</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    data-testid="analytics-date-input-end"
                    min={customStart}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setView('presets')}
                    className="flex-1 px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCustomApply}
                    disabled={!canApplyCustom}
                    data-testid="analytics-date-btn-apply"
                    className="flex-1 px-3 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function presetToAnalyticsDateRange(preset: DateRangePreset): AnalyticsDateRange {
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const p = PRESETS.find((x) => x.value === preset)!;

  switch (preset) {
    case '7d':
      return { startDate: now - 7 * dayMs, endDate: now, label: p.label, preset: '7d' };
    case '30d':
      return { startDate: now - 30 * dayMs, endDate: now, label: p.label, preset: '30d' };
    case '90d':
      return { startDate: now - 90 * dayMs, endDate: now, label: p.label, preset: '90d' };
    case 'all':
    default:
      return { label: p.label, preset: 'all' };
  }
}

/** Default analytics date range (Last 30 days) */
export const DEFAULT_ANALYTICS_DATE_RANGE: AnalyticsDateRange = presetToAnalyticsDateRange('30d');

/**
 * Convert analytics date range to API params (startDate, endDate, trendDays)
 */
export function analyticsDateRangeToParams(
  range: AnalyticsDateRange
): { startDate?: number; endDate?: number; trendDays: number } {
  const dayMs = 24 * 60 * 60 * 1000;

  if (range.startDate != null && range.endDate != null) {
    const trendDays = Math.min(Math.ceil((range.endDate - range.startDate) / dayMs), 90);
    return {
      startDate: range.startDate,
      endDate: range.endDate,
      trendDays,
    };
  }

  // Preset "all" or fallback
  const preset = range.preset || '30d';
  const now = Date.now();

  switch (preset) {
    case '7d':
      return {
        startDate: now - 7 * dayMs,
        endDate: now,
        trendDays: 7,
      };
    case '30d':
      return {
        startDate: now - 30 * dayMs,
        endDate: now,
        trendDays: 30,
      };
    case '90d':
      return {
        startDate: now - 90 * dayMs,
        endDate: now,
        trendDays: 90,
      };
    case 'all':
    default:
      return {
        trendDays: 365,
      };
  }
}

/** @deprecated Use analyticsDateRangeToParams instead. Kept for backwards compatibility. */
export function presetToDateRange(preset: DateRangePreset): { startDate?: number; endDate?: number; trendDays: number } {
  return analyticsDateRangeToParams(presetToAnalyticsDateRange(preset));
}
